import React, { useState } from 'react';
import {
  Card, Typography, Switch, Skeleton, App, Input, Button, Space, Tag, Radio,
  Alert, Tooltip, Empty,
} from 'antd';
import {
  CreditCardOutlined, KeyOutlined, ReloadOutlined, CopyOutlined,
  CheckCircleOutlined, LinkOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useIsModuleEnabled } from '../hooks/useIsModuleEnabled';
import { MODULE_KEYS } from '../constants/moduleKeys';
import {
  useGetMercadoPagoConfigQuery,
  useSaveMercadoPagoConfigMutation,
  useGetMpTerminalsQuery,
  useSetMpTerminalOperatingModeMutation,
} from '../services/mercadoPagoApi';

// Mismo canal de soporte que login/registro y la landing
const SUPPORT_WHATSAPP_URL = 'https://wa.me/522206294774';

const { Title, Text } = Typography;

/**
 * Configuración > Terminal Mercado Pago. Self-service: el restaurante pega el
 * access token y la clave de webhooks de su propia aplicación de Mercado Pago,
 * copia la URL de webhook a su panel de MP, pone su terminal en modo PDV y
 * activa el cobro. Soporte puede hacerlo por él con los mismos controles.
 */
const MercadoPagoSettings: React.FC = () => {
  const { isMobile } = useResponsive();
  const { message } = App.useApp();

  // Feature flag del módulo (solo soporte lo enciende). Sin él, la página se
  // muestra como "próximamente" — el backend además rechaza los endpoints.
  const { enabled: moduleEnabled, isLoading: moduleLoading } =
    useIsModuleEnabled(MODULE_KEYS.MercadoPagoPoint, { allowPlanBypass: false });

  const { data: config, isLoading } = useGetMercadoPagoConfigQuery(undefined, { skip: !moduleEnabled });
  const [saveConfig, { isLoading: saving }] = useSaveMercadoPagoConfigMutation();

  const canListTerminals = config?.hasAccessToken ?? false;
  const {
    data: terminals,
    isFetching: terminalsLoading,
    refetch: refetchTerminals,
    error: terminalsError,
  } = useGetMpTerminalsQuery(undefined, { skip: !canListTerminals });

  const [setOperatingMode, { isLoading: changingMode }] = useSetMpTerminalOperatingModeMutation();

  const [accessToken, setAccessToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  const extractError = (err: unknown): string =>
    (err as { data?: { message?: string } })?.data?.message
    ?? 'No se pudo completar la operación. Intenta de nuevo.';

  const handleSaveCredentials = async () => {
    if (!accessToken.trim() && !webhookSecret.trim()) {
      message.info('Pega el access token o la clave secreta que quieras actualizar');
      return;
    }
    try {
      await saveConfig({
        accessToken: accessToken.trim() || undefined,
        webhookSecret: webhookSecret.trim() || undefined,
      }).unwrap();
      setAccessToken('');
      setWebhookSecret('');
      message.success('Credenciales guardadas');
    } catch (err) {
      message.error(extractError(err));
    }
  };

  const handleToggleEnabled = async (checked: boolean) => {
    try {
      await saveConfig({ enabled: checked }).unwrap();
      message.success(checked ? 'Cobro con terminal activado' : 'Cobro con terminal desactivado');
    } catch (err) {
      message.error(extractError(err));
    }
  };

  const handleSetDefaultTerminal = async (terminalId: string) => {
    try {
      await saveConfig({ defaultTerminalId: terminalId }).unwrap();
      message.success('Terminal predeterminada actualizada');
    } catch (err) {
      message.error(extractError(err));
    }
  };

  const handleToggleMode = async (terminalId: string, currentMode: string) => {
    const newMode = currentMode === 'PDV' ? 'STANDALONE' : 'PDV';
    try {
      await setOperatingMode({ terminalId, operatingMode: newMode }).unwrap();
      message.success(newMode === 'PDV'
        ? 'Terminal en modo punto de venta: los cobros se envían desde el sistema.'
        : 'Terminal en modo independiente: cobra montos tecleados en la terminal.');
    } catch (err) {
      message.error(extractError(err));
    }
  };

  const copyWebhookUrl = async () => {
    if (!config?.webhookUrl) return;
    await navigator.clipboard.writeText(config.webhookUrl);
    message.success('URL copiada');
  };

  if (moduleLoading) {
    return (
      <div style={{ padding: isMobile ? 16 : 24 }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (!moduleEnabled) {
    return (
      <div style={{ padding: isMobile ? 16 : 24, maxWidth: 640 }}>
        <Title level={2} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CreditCardOutlined style={{ color: '#009ee3' }} />
          Terminal Mercado Pago
          <Tag color="blue">Próximamente</Tag>
        </Title>
        <Card style={{ borderRadius: isMobile ? 8 : 12 }}>
          <Space direction="vertical" size={12}>
            <Text>
              Estamos terminando de validar esta integración: podrás enviar el cobro
              de una cuenta directo a tu terminal Mercado Pago Point, y el comensal
              paga (y deja propina) ahí mismo — sin capturar nada a mano.
            </Text>
            <Text>
              ¿Te interesa usarla en tu restaurante? Contáctanos y te avisamos en
              cuanto esté lista; incluso puedes participar en las primeras pruebas.
            </Text>
            <Button
              type="primary"
              style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
              onClick={() => window.open(SUPPORT_WHATSAPP_URL, '_blank', 'noopener')}
            >
              Contactar a soporte
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <CreditCardOutlined style={{ color: 'var(--color-primary)' }} />
          Terminal Mercado Pago
        </Title>
        <Text type="secondary">
          Cobra las cuentas enviando el pago directo a tu terminal Point
        </Text>
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 860 }}>
        {/* Activación */}
        <Card style={{ borderRadius: isMobile ? 8 : 12 }}>
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 1 }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <CheckCircleOutlined style={{ fontSize: 22, color: 'var(--color-primary)', marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: 15 }}>Cobro con terminal Point</Text>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Al activarlo, en el modal de cobro aparece la opción "Terminal Point": el
                    monto llega solo a la terminal, el comensal paga (y agrega propina) ahí, y
                    el sistema registra el pago y cierra la cuenta automáticamente.
                  </Text>
                </div>
              </div>
              <Switch
                checked={config?.enabled ?? false}
                onChange={handleToggleEnabled}
                loading={saving}
                disabled={!config?.isConfigured && !(config?.enabled)}
                checkedChildren="Sí"
                unCheckedChildren="No"
              />
            </div>
          )}
          {!isLoading && !config?.isConfigured && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="Configura las credenciales y la URL de webhook para poder activar el cobro con terminal."
            />
          )}
        </Card>

        {/* Credenciales */}
        <Card
          title={<Space><KeyOutlined /> Credenciales de Mercado Pago</Space>}
          style={{ borderRadius: isMobile ? 8 : 12 }}
        >
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : (
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Crea una aplicación en el panel de desarrolladores de Mercado Pago
                (<a href="https://www.mercadopago.com.mx/developers/panel/app" target="_blank" rel="noreferrer">
                  Tus integraciones <LinkOutlined />
                </a>) con producto "Pagos presenciales — Point", copia el <Text strong>Access Token de producción</Text> y
                genera la <Text strong>clave secreta de Webhooks</Text> apuntando a la URL de abajo.
                Si casi siempre lo hace soporte por ti, también puedes pedirlo.
              </Text>

              <div>
                <Text strong style={{ fontSize: 13 }}>Access token</Text>
                {config?.hasAccessToken && (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    guardado{config.accessTokenHint ? ` ····${config.accessTokenHint}` : ''}
                  </Tag>
                )}
                <Input.Password
                  style={{ marginTop: 6 }}
                  placeholder={config?.hasAccessToken ? 'Pega uno nuevo solo para reemplazarlo' : 'APP_USR-...'}
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              <div>
                <Text strong style={{ fontSize: 13 }}>Clave secreta de webhooks</Text>
                {config?.hasWebhookSecret && <Tag color="green" style={{ marginLeft: 8 }}>guardada</Tag>}
                <Input.Password
                  style={{ marginTop: 6 }}
                  placeholder={config?.hasWebhookSecret ? 'Pega una nueva solo para reemplazarla' : 'Clave generada en Tus integraciones > Webhooks'}
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              <div>
                <Text strong style={{ fontSize: 13 }}>URL de webhook (pégala en Mercado Pago, evento "Orders")</Text>
                <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                  <Input readOnly value={config?.webhookUrl ?? ''} />
                  <Tooltip title="Copiar">
                    <Button icon={<CopyOutlined />} onClick={copyWebhookUrl} />
                  </Tooltip>
                </Space.Compact>
              </div>

              <Button type="primary" onClick={handleSaveCredentials} loading={saving}>
                Guardar credenciales
              </Button>
            </Space>
          )}
        </Card>

        {/* Terminales */}
        <Card
          title={<Space><CreditCardOutlined /> Terminales</Space>}
          extra={
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={() => refetchTerminals()}
              loading={terminalsLoading}
              disabled={!canListTerminals}
            >
              Actualizar
            </Button>
          }
          style={{ borderRadius: isMobile ? 8 : 12 }}
        >
          {!canListTerminals ? (
            <Text type="secondary">Guarda el access token para ver tus terminales vinculadas.</Text>
          ) : terminalsError ? (
            <Alert type="warning" showIcon message={extractError(terminalsError)} />
          ) : terminalsLoading && !terminals ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : !terminals || terminals.length === 0 ? (
            <Empty description="No hay terminales vinculadas a esta cuenta de Mercado Pago" />
          ) : (
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Para cobrar desde el sistema la terminal debe estar en modo <Text strong>punto de venta (PDV)</Text>.
                La predeterminada recibe los cobros cuando no se elige otra.
              </Text>
              <Radio.Group
                value={config?.defaultTerminalId}
                onChange={(e) => handleSetDefaultTerminal(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {terminals.map((t) => (
                    <div
                      key={t.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 12px',
                        border: '1px solid var(--border-color, #d9d9d9)',
                        borderRadius: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Radio value={t.id}>
                        <Text strong style={{ fontFamily: 'monospace', fontSize: 13 }}>{t.id}</Text>
                      </Radio>
                      <Tag color={t.operatingMode === 'PDV' ? 'green' : 'orange'}>
                        {t.operatingMode === 'PDV' ? 'Punto de venta' : 'Independiente'}
                      </Tag>
                      <div style={{ flex: 1 }} />
                      <Button
                        size="small"
                        loading={changingMode}
                        onClick={() => handleToggleMode(t.id, t.operatingMode)}
                      >
                        {t.operatingMode === 'PDV' ? 'Cambiar a independiente' : 'Usar como punto de venta'}
                      </Button>
                    </div>
                  ))}
                </Space>
              </Radio.Group>
            </Space>
          )}
        </Card>
      </Space>
    </div>
  );
};

export default MercadoPagoSettings;
